import React, { useRef, useState } from 'react';
import { FlatList, View, ActivityIndicator, RefreshControl, StyleSheet, ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
export interface DbFlatListProps<T> {
    /** 数据源数组 */
    data: T[];
    /** 自定义渲染每一项的回调函数，返回当前项的渲染节点 */
    renderItem: (item: T, index: number) => React.ReactNode;
    /** 自定义每一项的唯一key生成函数，用于React列表优化，可选 */
    keyExtractor?: (item: T, index: number) => string;
    /** 列表滚动到底部时触发的回调函数，用于加载更多数据，可选 */
    onEndReached?: () => void;
    /** 列表下拉刷新时触发的回调函数，用于重新加载数据，可选 */
    onRefresh?: () => void;
    /** 控制列表是否显示下拉刷新状态，配合onRefresh使用，可选 */
    refreshing?: boolean;
    /** 控制列表是否显示加载更多状态，配合onEndReached使用，可选 */
    loadingMore?: boolean;
    /** 列表列数，控制一行展示多少列，默认1列（单列列表），可选 */
    numColumns?: number;
    /** 列表项之间的间距（水平/垂直），单位px，可选 */
    itemSpacing?: number;
    /** 列表容器两侧的间距（左右边距），单位px，可选 */
    sideSpacing?: number;
    /** 列表容器的样式，透传ScrollView/FlatList的style属性，可选 */
    style?: ViewStyle;
    /** 列表内容容器的样式，透传ScrollView/FlatList的contentContainerStyle属性，可选 */
    contentContainerStyle?: ViewStyle;
    /** 列表底部自定义组件，通常用于展示加载更多/无更多数据提示，可选 */
    ListFooterComponent?: React.ReactNode;
    /** 是否启用瀑布流布局（简单实现，仅适配不等高item，复杂场景建议使用第三方瀑布流库），可选 */
    isWaterfall?: boolean;
    /** 下拉刷新触发的距离阈值（单位px），默认100px，值越大需要下拉越长才触发 */
    refreshThreshold?: number;
    /** 下拉刷新防抖时间（单位ms），默认500ms，防止短时间重复触发 */
    refreshDebounceTime?: number;
    /** 加载更多触发阈值（0-1），默认0.2（列表底部20%可见时触发），值越大触发越晚 */
    onEndReachedThreshold?: number;
}

// 独立定义 Footer 组件
const Footer = ({ loadingMore, ListFooterComponent }: { loadingMore: boolean; ListFooterComponent?: React.ReactNode }) => {
    if (!loadingMore) { return null; }

    return ListFooterComponent ? (
        <>{ListFooterComponent}</>
    ) : (
        <View style={styles.footerContainer}>
            <ActivityIndicator size="small" />
        </View>
    );
};

const DbFlatList = <T,>({
    data,
    renderItem,
    keyExtractor,
    onEndReached,
    onRefresh, // 核心：通过是否传onRefresh判断是否开启下拉刷新
    refreshing = false,
    loadingMore = false,
    numColumns = 1,
    itemSpacing = 8,
    sideSpacing = 12,
    style,
    contentContainerStyle,
    ListFooterComponent,
    isWaterfall = false,
    refreshThreshold = 100,
    refreshDebounceTime = 500,
    onEndReachedThreshold = 0.2,
}: DbFlatListProps<T>) => {
    const insets = useSafeAreaInsets(); // 新增：获取安全区域（包含底部导航栏高度）
    // 1. 刷新防抖锁：防止短时间重复触发刷新
    const lastRefreshTime = useRef<number>(0);
    // 2. 加载更多锁：防止重复触发加载更多
    const isLoadingMore = useRef<boolean>(false);

    // 处理防抖后的下拉刷新
    const handleRefresh = React.useCallback(() => {
        if (!onRefresh) { return; }

        const now = Date.now();
        // 防抖判断：距离上次刷新不足指定时间，不触发
        if (now - lastRefreshTime.current < refreshDebounceTime) {
            return;
        }

        lastRefreshTime.current = now;
        onRefresh();
    }, [onRefresh, refreshDebounceTime]);

    // 处理防抖后的加载更多
    const handleEndReached = React.useCallback(() => {
        if (!onEndReached || loadingMore || isLoadingMore.current) { return; }

        isLoadingMore.current = true;
        onEndReached();
        // 重置加载锁（防止一直锁死，可根据实际接口耗时调整）
        setTimeout(() => {
            isLoadingMore.current = false;
        }, 1000);
    }, [onEndReached, loadingMore]);

    // 瀑布流简单实现：左右交替分组
    let renderList = renderItem;
    let flatData = data;

    if (isWaterfall && numColumns > 1) {
        // 简单分组，复杂瀑布流建议用 react-native-masonry-list
        const columns: T[][] = Array.from({ length: numColumns }, () => []);
        data.forEach((item, idx) => {
            columns[idx % numColumns].push(item);
        });
        flatData = [];
        columns.forEach(col => flatData.push(...col));
        renderList = (item: T, idx: number) => renderItem(item, idx);
    }

    return (
        <FlatList
            data={flatData}
            renderItem={({ item, index }) => (
                <View
                    style={{
                        marginBottom: itemSpacing,
                        marginLeft: (index % numColumns === 0) ? sideSpacing : itemSpacing / 2,
                        marginRight: (index % numColumns === numColumns - 1) ? sideSpacing : itemSpacing / 2,
                    }}
                >
                    {renderList(item, index)}
                </View>
            )}
            keyExtractor={keyExtractor || ((item, idx) => idx.toString())}
            numColumns={numColumns}
            style={style}
            contentContainerStyle={[
                { paddingTop: itemSpacing, paddingBottom: insets.bottom + itemSpacing + 20 },
                contentContainerStyle,
            ]}
            // 加载更多：防抖 + 阈值控制
            onEndReached={handleEndReached}
            onEndReachedThreshold={onEndReachedThreshold}
            // 核心修改1：仅当传入onRefresh时，才渲染RefreshControl（关闭时完全移除）
            refreshControl={
                onRefresh ? (
                    <RefreshControl
                        refreshing={refreshing}
                        onRefresh={handleRefresh}
                        progressViewOffset={refreshThreshold}
                        tintColor="#000"
                        colors={['#000']}
                    />
                ) : undefined
            }
            // 核心修改2：关闭下拉刷新时，禁用列表回弹动画（bounces=false）
            bounces={!!onRefresh} // 有onRefresh时回弹，无则禁用
            // 核心修改3：关闭下拉刷新时，禁用过度滚动效果（安卓）
            overScrollMode={onRefresh ? 'always' : 'never'}
            ListFooterComponent={<Footer loadingMore={loadingMore} ListFooterComponent={ListFooterComponent} />}
            scrollEventThrottle={16}
        />
    );
};

// 样式提取到 StyleSheet
const styles = StyleSheet.create({
    footerContainer: {
        padding: 16,
        alignItems: 'center',
        paddingBottom: 32, // 新增：增加底部内边距
    },
});

export default DbFlatList;
